import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { DomainSummary } from "@/api/intelligence";
import { formatCount, formatHours, formatInr, formatPercentChange } from "@/lib/intelligenceFormat";
import { ArrowDownRight, ArrowUpRight, Minus, Pill, FlaskConical, IndianRupee } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
	summaries: DomainSummary[];
	basePath?: string;
	comparisonLabel?: string | null;
};

const DOMAIN_ICONS = {
	PHARMACY: Pill,
	LAB: FlaskConical,
	FINANCE: IndianRupee,
} as const;

function formatSummaryValue(unit: string, value: number): string {
	if (unit === "CURRENCY_INR") return formatInr(value, 0);
	if (unit === "PERCENT") return `${value}%`;
	if (unit === "HOURS") return formatHours(value);
	return formatCount(value);
}

function TrendIcon({ trend }: { trend: DomainSummary["metrics"][0]["trend"] }) {
	if (trend === "up") return <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />;
	if (trend === "down") return <ArrowDownRight className="h-3.5 w-3.5 text-rose-600" />;
	return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
}

export function DomainSummariesSection({ summaries = [], basePath = "/hospital-admin", comparisonLabel }: Props) {
	return (
		<section className="space-y-4">
			<div>
				<h2 className="text-lg font-semibold text-slate-900">Domain summaries</h2>
				<p className="text-sm text-muted-foreground">
					Executive indicators for Pharmacy, Lab, and Finance — open each domain for full detail.
				</p>
			</div>

			<div className="grid gap-4 lg:grid-cols-3">
				{summaries.map((summary) => {
					const Icon = DOMAIN_ICONS[summary.domain];
					const drillDown = summary.drillDownPath.replace("/hospital-admin", basePath);

					return (
						<Card key={summary.domain} className="border-slate-200/80 shadow-sm">
							<CardHeader className="pb-2">
								<div className="flex items-center justify-between gap-2">
									<CardTitle className="text-base font-medium flex items-center gap-2">
										<Icon className="h-4 w-4 text-indigo-600" />
										{summary.label}
									</CardTitle>
									<Link
										to={drillDown}
										className="text-xs font-medium text-indigo-600 hover:underline shrink-0"
									>
										Open →
									</Link>
								</div>
							</CardHeader>
							<CardContent className="space-y-3">
								{summary.metrics.map((metric) => (
									<div
										key={metric.key}
										className="flex items-start justify-between gap-3 text-sm border-b border-slate-100 last:border-0 pb-2 last:pb-0"
									>
										<div className="min-w-0">
											<p className="text-muted-foreground text-xs">{metric.label}</p>
											<p className="font-semibold tabular-nums text-slate-900">
												{formatSummaryValue(metric.unit, metric.value)}
											</p>
										</div>
										{metric.percentChange !== null && (
											<div
												className={cn(
													"flex items-center gap-1 text-xs tabular-nums shrink-0",
													metric.trend === "up" && "text-emerald-700",
													metric.trend === "down" && "text-rose-700",
													metric.trend === "flat" && "text-muted-foreground"
												)}
												title={comparisonLabel ? `vs ${comparisonLabel}` : undefined}
											>
												<TrendIcon trend={metric.trend} />
												{formatPercentChange(metric.percentChange)}
											</div>
										)}
									</div>
								))}
							</CardContent>
						</Card>
					);
				})}
			</div>
		</section>
	);
}
